import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { QA_COLUMNS, QA_DOC_TITLE, QA_INTRO_SECTIONS, QA_STATUS_LABELS, QaStatus } from "@/lib/basProcess/constants";
import { MessagesSquare, Plus, Trash2, FileDown } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import { generateQaLogWordDocument } from "@/lib/documentGenerator/overlamningWordGenerator";

interface Props {
  projectId: string;
}

export interface QaEntry {
  id: string;
  entry_number: number;
  subject: string | null;
  decision_ref: string | null;
  decision_summary: string | null;
  change_description: string | null;
  responsible_function: string | null;
  afs_reference: string | null;
  comment: string | null;
  decision_date: string | null;
  status: string;
}

const SELECT_COLUMNS =
  "id, entry_number, subject, decision_ref, decision_summary, change_description, responsible_function, afs_reference, comment, decision_date, status";

export const QaLog: React.FC<Props> = ({ projectId }) => {
  const [entries, setEntries] = useState<QaEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data, error } = await supabase
      .from("qa_log_entries")
      .select(SELECT_COLUMNS)
      .eq("project_id", projectId)
      .order("entry_number", { ascending: true });
    if (error) toast.error("Kunde inte läsa loggen");
    else setEntries((data || []) as unknown as QaEntry[]);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const create = async () => {
    const nextNumber = entries.reduce((m, e) => Math.max(m, e.entry_number), 0) + 1;
    const { error } = await supabase.from("qa_log_entries").insert({
      project_id: projectId,
      entry_number: nextNumber,
      decision_date: new Date().toISOString().slice(0, 10),
    });
    if (error) return toast.error("Kunde inte skapa raden");
    await load();
  };

  const update = async (id: string, patch: Partial<QaEntry>) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    const { error } = await supabase.from("qa_log_entries").update(patch as any).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const remove = async (id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
    await supabase.from("qa_log_entries").delete().eq("id", id);
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar logg...</div>;

  const open = entries.filter((e) => e.status === "open").length;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="25"
        exportHistory={{ documentType: "qa_logg" }}
        title={QA_DOC_TITLE}
        description="Q&A-logg enligt mall 25 – beslut och avsteg med arbetsmiljömässig relevans."
        icon={MessagesSquare}
        meta={<Badge variant={open > 0 ? "destructive" : "outline"}>{open} öppna</Badge>}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => generateQaLogWordDocument(entries)} disabled={entries.length === 0}>
              <FileDown className="h-4 w-4 mr-1" />Word
            </Button>
            <Button onClick={create}>
              <Plus className="h-4 w-4 mr-1" />Ny rad
            </Button>
          </div>
        }
      />

      <Card>
        <CardContent className="py-5 space-y-4">
          {QA_INTRO_SECTIONS.map((s) => (
            <div key={s.title} className="space-y-1">
              <h4 className="text-sm font-semibold">{s.title}</h4>
              {s.paragraphs.map((p, i) => (
                <p key={i} className="text-xs leading-relaxed text-muted-foreground">
                  {p}
                </p>
              ))}
              {s.bullets && (
                <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-0.5">
                  {s.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <MessagesSquare className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Inga ändringar eller avsteg loggade ännu.</p>
            <Button onClick={create}>Lägg till första raden</Button>
          </CardContent>
        </Card>
      ) : (
        entries.map((e) => (
          <Card key={e.id}>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-base">Ärende {e.entry_number}</CardTitle>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    className="w-full sm:w-[160px]"
                    defaultValue={e.decision_date ?? ""}
                    onBlur={(ev) => update(e.id, { decision_date: ev.target.value || null })}
                  />
                  <Select value={e.status} onValueChange={(v) => update(e.id, { status: v })}>
                    <SelectTrigger className="w-full sm:w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(QA_STATUS_LABELS) as QaStatus[]).map((s) => (
                        <SelectItem key={s} value={s}>
                          {QA_STATUS_LABELS[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" onClick={() => remove(e.id)}>
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              {QA_COLUMNS.map((c) => (
                <div key={c.key} className={`space-y-1 ${c.multiline ? "sm:col-span-2" : ""}`}>
                  <label className="text-sm font-medium">{c.label}</label>
                  <p className="text-xs text-muted-foreground">{c.hint}</p>
                  {c.multiline ? (
                    <Textarea
                      rows={2}
                      defaultValue={(e as any)[c.key] ?? ""}
                      onBlur={(ev) => update(e.id, { [c.key]: ev.target.value } as Partial<QaEntry>)}
                    />
                  ) : (
                    <Input
                      defaultValue={(e as any)[c.key] ?? ""}
                      onBlur={(ev) => update(e.id, { [c.key]: ev.target.value } as Partial<QaEntry>)}
                    />
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default QaLog;
